/**
 * Which contract governs one elevator today.
 *
 * A car can be listed on more than one contract — an expired agreement and its
 * replacement, or two overlapping ones — so "the contract" needs a rule, and it has to
 * be the same rule the backend uses or the same elevator gets judged against two
 * different targets depending on which screen is asking.
 *
 * The rule, from the SQL in `services/api/kpis/maintenance_completion.py`: live today,
 * most recently started wins, and a contract with no recorded start sorts oldest rather
 * than being dropped.
 *
 * The ORM query behind the Elevators grid's own targets (`main/views/api/cars.py`) does
 * not follow this — it omits the soft-delete check and drops rows whose `start_date` is
 * null. This module follows the SQL, which is the correct of the two.
 */
import type { Contract } from '~/data/schemas/contract';

/** Date-only strings are compared as dates, never as timestamps. */
const parseDay = (value: string | null | undefined): Date | null => {
  if (!value) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (!match) return null;
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
};

const startOfDay = (date: Date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());

/** Sorts a contract with no start date oldest, mirroring the SQL's COALESCE. */
const NO_START_SORTS_OLDEST = new Date(1900, 0, 1).getTime();

export function selectGoverningContractForCar(
  contracts: readonly Contract[],
  propertyId: string | undefined,
  carId: string | undefined,
  today: Date = new Date(),
): Contract | null {
  if (!propertyId || !carId) return null;

  const asOf = startOfDay(today);

  const candidates = contracts.flatMap((contract) => {
    const covering = (contract.propertyContracts ?? []).filter((propertyContract) => {
      if (propertyContract.propertyId !== propertyId) return false;
      if (!propertyContract.cars.some((car) => car.carId === carId)) return false;

      const start = parseDay(propertyContract.startDate);
      const expiration = parseDay(propertyContract.expirationDate);

      // A null bound is unbounded on that side, not a failed comparison.
      if (start !== null && start > asOf) return false;
      if (expiration !== null && expiration < asOf) return false;
      return true;
    });

    return covering.map((propertyContract) => ({
      contract,
      startedAt: parseDay(propertyContract.startDate)?.getTime() ?? NO_START_SORTS_OLDEST,
    }));
  });

  if (candidates.length === 0) return null;

  return candidates.reduce((newest, candidate) =>
    candidate.startedAt > newest.startedAt ? candidate : newest,
  ).contract;
}
